// Location: src/features/labels/LabelCatalogManager.tsx
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Tooltip,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalOffer as LabelIcon,
} from '@mui/icons-material';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { getCurrentUser } from '@/utils/auth';
import type { CatalogLabel, LabelOwnerType, LabelScope } from './types';
import { LabelChip } from './LabelChip';

const FREE = '__free__';
const DEFAULT_COLOR = '#0BA5B7';
const PALETTE = ['#0BA5B7', '#2E7D32', '#C62828', '#6A1B9A', '#EF6C00', '#1565C0', '#00838F', '#5D4037', '#455A64'];

interface ScopeDialogState {
  open: boolean;
  editing?: LabelScope;
}

export const LabelCatalogManager = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const dataProvider = useDataProvider() as any;
  const notify = useNotify();

  const user = getCurrentUser();
  const isSuperAdmin = user?.role === 'SuperAdmin' || user?.role === 'Admin';

  const [loading, setLoading] = useState(true);
  const [scopes, setScopes] = useState<LabelScope[]>([]);
  const [groups, setGroups] = useState<{ id: string; name: string }[]>([]);
  const [selected, setSelected] = useState<string>(FREE);
  const [values, setValues] = useState<CatalogLabel[]>([]);
  const [newValue, setNewValue] = useState('');
  const [newValueColor, setNewValueColor] = useState(DEFAULT_COLOR);
  const [busy, setBusy] = useState(false);

  const [scopeDialog, setScopeDialog] = useState<ScopeDialogState>({ open: false });

  const loadScopes = useCallback(async () => {
    const { data } = await dataProvider.getLabelScopes();
    setScopes(data);
  }, [dataProvider]);

  const loadValues = useCallback(
    async (scopeKey: string) => {
      const { data } = await dataProvider.getLabels(scopeKey && scopeKey !== FREE ? { scopeId: scopeKey } : {});
      setValues(scopeKey === FREE ? data.filter((l: CatalogLabel) => !l.scopeId) : data);
    },
    [dataProvider]
  );

  useEffect(() => {
    (async () => {
      try {
        await loadScopes();
        try {
          const g = await dataProvider.getList('groups', {
            filter: {},
            sort: { field: 'name', order: 'ASC' },
            pagination: { page: 1, perPage: 1000 },
          });
          setGroups(g.data.map((x: any) => ({ id: x.id, name: x.name })));
        } catch {
          /* groups optional */
        }
      } catch {
        notify('resources.labels.errors.loadFailed', { type: 'warning' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadValues(selected);
  }, [selected, loadValues]);

  const selectedScope = useMemo(() => scopes.find((s) => s.id === selected), [scopes, selected]);

  const addValue = async () => {
    const v = newValue.trim();
    if (!v) return;
    setBusy(true);
    try {
      const input: Record<string, unknown> =
        selected !== FREE
          ? { scopeId: selected, value: v, color: newValueColor }
          : {
              value: v,
              color: newValueColor,
              ownerType: (isSuperAdmin ? 'global' : 'group') as LabelOwnerType,
              ownerGroupId: isSuperAdmin ? undefined : groups[0]?.id,
            };
      await dataProvider.createLabel(input);
      setNewValue('');
      await loadValues(selected);
    } catch (e: any) {
      notify(e?.body?.error?.message || 'resources.labels.errors.createFailed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const deleteValue = async (id: string) => {
    setBusy(true);
    try {
      await dataProvider.deleteLabel(id);
      await loadValues(selected);
    } catch {
      notify('resources.labels.errors.deleteFailed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const deleteScope = async (s: LabelScope) => {
    if (!window.confirm(translate('resources.labels.catalog.confirmDeleteScope', { key: s.key }))) return;
    setBusy(true);
    try {
      await dataProvider.deleteLabelScope(s.id);
      if (selected === s.id) setSelected(FREE);
      await loadScopes();
    } catch {
      notify('resources.labels.errors.deleteFailed', { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 1 }}>
      {/* Header — icon + heading + one-line description, shared across every admin-options tab */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <LabelIcon color="primary" />
        <Typography variant="h6">{translate('resources.labels.catalog.pageTitle')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {translate('resources.labels.catalog.pageDescription')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
      {/* Left: scopes list */}
      <Paper variant="outlined" sx={{ width: { xs: '100%', md: 280 }, flexShrink: 0, p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {translate('resources.labels.catalog.scopes')}
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setScopeDialog({ open: true })}
            sx={{ textTransform: 'none' }}
          >
            {translate('resources.labels.catalog.newScope')}
          </Button>
        </Box>
        <Divider sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <ScopeRow
            label={translate('resources.labels.catalog.freeLabels')}
            active={selected === FREE}
            onClick={() => setSelected(FREE)}
          />
          {scopes.map((s) => (
            <ScopeRow
              key={s.id}
              label={s.displayName || s.key}
              hint={s.exclusive ? translate('resources.labels.catalog.exclusiveShort') : undefined}
              color={s.color}
              active={selected === s.id}
              onClick={() => setSelected(s.id)}
              onEdit={() => setScopeDialog({ open: true, editing: s })}
              onDelete={() => deleteScope(s)}
            />
          ))}
        </Box>
      </Paper>

      {/* Right: values for the selected scope */}
      <Paper variant="outlined" sx={{ flex: 1, p: 1.5, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {selected === FREE
            ? translate('resources.labels.catalog.freeLabels')
            : `${selectedScope?.displayName || selectedScope?.key || ''}`}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {selected === FREE
            ? translate('resources.labels.catalog.freeLabelsHint')
            : translate('resources.labels.catalog.valuesHint')}
        </Typography>

        {/* Existing values */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, my: 1.5, minHeight: 28 }}>
          {values.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              {translate('resources.labels.catalog.noValues')}
            </Typography>
          ) : (
            values.map((v) => (
              <LabelChip
                key={v.id}
                scope={selected === FREE ? undefined : selectedScope?.key}
                value={v.value}
                color={v.color}
                size="medium"
                onDelete={busy ? undefined : () => deleteValue(v.id)}
              />
            ))
          )}
        </Box>

        <Divider sx={{ my: 1 }} />

        {/* Add value */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder={translate('resources.labels.catalog.newValuePlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addValue();
              }
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <ColorPicker value={newValueColor} onChange={setNewValueColor} />
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            disabled={busy || !newValue.trim()}
            onClick={addValue}
            sx={{ textTransform: 'none' }}
          >
            {translate('ra.action.add')}
          </Button>
        </Box>
      </Paper>
      </Box>

      {scopeDialog.open && (
        <ScopeDialog
          editing={scopeDialog.editing}
          isSuperAdmin={isSuperAdmin}
          groups={groups}
          onClose={() => setScopeDialog({ open: false })}
          onSaved={async () => {
            setScopeDialog({ open: false });
            await loadScopes();
          }}
        />
      )}
    </Box>
  );
};

// ---- Scope row ----

interface ScopeRowProps {
  label: string;
  hint?: string;
  color?: string | null;
  active: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const ScopeRow = ({ label, hint, color, active, onClick, onEdit, onDelete }: ScopeRowProps) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 1,
      py: 0.75,
      borderRadius: 1,
      cursor: 'pointer',
      bgcolor: active ? 'action.selected' : 'transparent',
      '&:hover': { bgcolor: 'action.hover' },
      '&:hover .scope-actions': { opacity: 1 },
    }}
  >
    <LabelIcon sx={{ fontSize: 16, color: color || 'text.secondary' }} />
    <Typography variant="body2" sx={{ flex: 1, fontWeight: active ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {label}
    </Typography>
    {hint && (
      <Typography variant="caption" color="text.secondary">
        {hint}
      </Typography>
    )}
    {(onEdit || onDelete) && (
      <Box className="scope-actions" sx={{ display: 'flex', opacity: 0, transition: 'opacity .15s' }}>
        {onEdit && (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }} sx={{ p: 0.25 }}>
            <EditIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
        {onDelete && (
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ p: 0.25 }}>
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        )}
      </Box>
    )}
  </Box>
);

// ---- Color picker ----

const ColorPicker = ({ value, onChange }: { value: string; onChange: (c: string) => void }) => (
  <Box sx={{ display: 'flex', gap: 0.5 }}>
    {PALETTE.map((c) => (
      <Tooltip key={c} title={c}>
        <Box
          onClick={() => onChange(c)}
          sx={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            bgcolor: c,
            cursor: 'pointer',
            border: value === c ? '2px solid' : '1px solid',
            borderColor: value === c ? 'text.primary' : 'divider',
          }}
        />
      </Tooltip>
    ))}
  </Box>
);

// ---- Scope create/edit dialog ----

interface ScopeDialogProps {
  editing?: LabelScope;
  isSuperAdmin: boolean;
  groups: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}

const ScopeDialog = ({ editing, isSuperAdmin, groups, onClose, onSaved }: ScopeDialogProps) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider() as any;
  const notify = useNotify();

  const [key, setKey] = useState(editing?.key ?? '');
  const [displayName, setDisplayName] = useState(editing?.displayName ?? '');
  const [color, setColor] = useState(editing?.color ?? DEFAULT_COLOR);
  const [exclusive, setExclusive] = useState(editing?.exclusive ?? true);
  const [ownerType, setOwnerType] = useState<LabelOwnerType>(
    editing?.ownerType ?? (isSuperAdmin ? 'global' : 'group')
  );
  const [ownerGroupId, setOwnerGroupId] = useState<string>(editing?.ownerGroupId ?? groups[0]?.id ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (editing) {
        await dataProvider.updateLabelScope(editing.id, { displayName, color, exclusive });
      } else {
        await dataProvider.createLabelScope({
          key,
          displayName: displayName || undefined,
          color,
          exclusive,
          ownerType,
          ownerGroupId: ownerType === 'group' ? ownerGroupId : undefined,
        });
      }
      onSaved();
    } catch (e: any) {
      notify(e?.body?.error?.message || 'resources.labels.errors.createFailed', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {translate(editing ? 'resources.labels.catalog.editScope' : 'resources.labels.catalog.newScope')}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label={translate('resources.labels.fields.key')}
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={!!editing}
            size="small"
            fullWidth
            helperText={translate('resources.labels.hints.key')}
          />
          <TextField
            label={translate('resources.labels.fields.displayName')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            size="small"
            fullWidth
          />
          <Box>
            <Typography variant="caption" color="text.secondary">
              {translate('resources.labels.fields.color')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <ColorPicker value={color} onChange={setColor} />
            </Box>
          </Box>
          <FormControlLabel
            control={<Switch checked={exclusive} onChange={(e) => setExclusive(e.target.checked)} />}
            label={translate('resources.labels.fields.exclusive')}
          />
          {!editing && (
            <>
              {isSuperAdmin && (
                <Select size="small" value={ownerType} onChange={(e) => setOwnerType(e.target.value as LabelOwnerType)} fullWidth>
                  <MenuItem value="global">{translate('resources.labels.owner.global')}</MenuItem>
                  <MenuItem value="group">{translate('resources.labels.owner.group')}</MenuItem>
                </Select>
              )}
              {ownerType === 'group' && (
                <Select size="small" value={ownerGroupId} onChange={(e) => setOwnerGroupId(e.target.value)} fullWidth displayEmpty>
                  {groups.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || (!editing && !key.trim()) || (ownerType === 'group' && !ownerGroupId)}
          sx={{ textTransform: 'none' }}
        >
          {translate('ra.action.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LabelCatalogManager;
